import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { toast } from "sonner";
import { LogOut, Users, MapPin, Wrench, Clock, Trash2, Plus, LayoutDashboard, Bell, CheckCircle, XCircle, ExternalLink, Calendar as CalendarIcon, ChevronDown, ChevronRight, BarChart3, Car, Edit2, Download, FileText, ClipboardList, Play, Image, AlertTriangle, HelpCircle, Settings, Calculator, GraduationCap, Crown, Package, Eye, DollarSign, Receipt, Layers, Building2, Coins, Activity } from "lucide-react";
import AttendanceCalendarGrid from "@/components/AttendanceCalendarGrid";
import ProjectReport from "@/components/ProjectReport";
import TripSheetsPanel from "@/components/TripSheetsPanel";
import { ToolsTab, TasksTab, WorkersTab, LeavesTab, DocumentsTab, DevizTab, InventarTab, FacturiTab, ProjectPhasesTab, HomeTab, NotificationsTab, SitesTab } from "@/components/dashboard";
import DevizProTab from "@/components/dashboard/DevizProTab";
import SketchesTab from "@/components/dashboard/sketches/SketchesTab";
import PlatiTab from "@/components/dashboard/PlatiTab";
import StatDePlataTab from "@/components/dashboard/StatDePlataTab";
import GanttChart from "@/components/dashboard/GanttChart";
import DashboardStats from "@/components/dashboard/DashboardStats";
import { HelpTooltip } from "@/components/HelpTooltip";
import SettingsDialog from "@/components/SettingsDialog";
import TutorialOverlay from "@/components/TutorialOverlay";
import CompanySettingsDialog from "@/components/dashboard/CompanySettingsDialog";
import BackgroundJobsPanel from "@/components/dashboard/BackgroundJobsPanel";
import { Switch } from "@/components/ui/switch";
import SignaturePad from "@/components/worker/SignaturePad";
import { PenTool, Sparkles, FileSpreadsheet, Pencil } from "lucide-react";
import { safeOpenHTMLWindow } from "@/utils/safeHTML";
import GlassLayout from "@/components/GlassLayout";
import { formatDateRO, formatDateTimeRO } from "@/utils/dateFormat";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to get user from either storage (same as App.js)
function getStoredUser() {
  const fromLocal = localStorage.getItem('user');
  const fromSession = sessionStorage.getItem('user');
  const raw = fromLocal || fromSession;
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (error) {
    console.error('Failed to parse stored user:', error);
    return null;
  }
}

export default function MasterDashboard() {
  const navigate = useNavigate();
  const user = getStoredUser() || {};
  const companyId = user.company_id; // Multi-tenancy: ID-ul companiei curente
  
  // Controlled tab state for browser back button handling
  const [activeTab, setActiveTab] = useState("dashboard");
  // Pending deviz id to auto-open in editor (from contract "Deschide în DevizePRO")
  const [pendingEditDevizId, setPendingEditDevizId] = useState(null);
  // Bump counter to force reload even if same id
  const [editDevizNonce, setEditDevizNonce] = useState(0);

  const handleNavigateToDeviz = (devizId) => {
    setPendingEditDevizId(devizId);
    setEditDevizNonce((n) => n + 1);
    setActiveTab("deviz");
  };
  
  const [sites, setSites] = useState([]);
  const [tools, setTools] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [subcontractors, setSubcontractors] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pendingLeaves, setPendingLeaves] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [documentStats, setDocumentStats] = useState({ total: 0, by_type: {} });
  const [tasks, setTasks] = useState([]);
  const [taskStats, setTaskStats] = useState({ total: 0, open: 0, in_progress: 0, completed: 0 });
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [newTask, setNewTask] = useState({ title: "", description: "", site_id: "", location_details: "", priority: "normal", assigned_to: [] });
  const [selectedWorkerForCalendar, setSelectedWorkerForCalendar] = useState(null);
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth() + 1);
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
  const [calendarData, setCalendarData] = useState(null);
  const [allWorkersCalendarData, setAllWorkersCalendarData] = useState({});
  const [yearlyLeaveData, setYearlyLeaveData] = useState({});
  const [loadingCalendar, setLoadingCalendar] = useState(false);
  
  // Site contracts dialog state
  const [selectedSiteForContracts, setSelectedSiteForContracts] = useState(null);
  const [siteContracts, setSiteContracts] = useState([]);
  const [showCompanySettings, setShowCompanySettings] = useState(false);
  const [showBackgroundJobs, setShowBackgroundJobs] = useState(false);
  const [jobsProcessingCount, setJobsProcessingCount] = useState(0);
  
  // Demo data state
  const [hasDemoData, setHasDemoData] = useState(false);
  const [showDemoBanner, setShowDemoBanner] = useState(true);
  const [deletingDemo, setDeletingDemo] = useState(false);
  
  // Settings dialog state
  const [showSettings, setShowSettings] = useState(false);
  
  // Tutorial state
  const [showTutorial, setShowTutorial] = useState(false);
  
  // Devize state (for Facturi)
  const [devize, setDevize] = useState([]);
  
  // Credits balance state
  const [creditsBalance, setCreditsBalance] = useState(0);
  // Banner de avertizare sold mic (<30 credite) — controlat de user (dismiss)
  const [creditsBannerDismissed, setCreditsBannerDismissed] = useState(() => {
    try { return sessionStorage.getItem('creditsBannerDismissed') === '1'; } catch { return false; }
  });
  // Pragul minim sub care alertăm
  const LOW_CREDITS_THRESHOLD = 30;
  // Toast-ul de avertizare se afișează o singură dată per sesiune când trecem sub prag
  const lastToastedRef = useRef(null);
  
  // Plati dialog state
  const [showPlatiDialog, setShowPlatiDialog] = useState(false);
  
  // Site phases state
  const [selectedSiteForPhases, setSelectedSiteForPhases] = useState(null);
  
  // Auto-pontaj state
  const [autoAttendanceSettings, setAutoAttendanceSettings] = useState(null);
  const [showAutoPontaj, setShowAutoPontaj] = useState(false);
  const [loadingAutoPontaj, setLoadingAutoPontaj] = useState(false);
  
  // Admin signature state
  const [showAdminSignature, setShowAdminSignature] = useState(false);
  const [adminSignatureStatus, setAdminSignatureStatus] = useState(null);
  
  // Fetch credits balance
  const fetchCreditsBalance = useCallback(() => {
    if (companyId) {
      axios.get(`${API}/credits/balance`, { params: { company_id: companyId } })
        .then(res => {
          const bal = res.data?.balance || 0;
          setCreditsBalance(bal);
          // Când balance revine peste prag, resetăm flag-ul de dismiss + ref-ul de toast
          // (efectele negative ale state-set-in-effect sunt evitate făcând asta în callback-ul fetch).
          if (bal >= LOW_CREDITS_THRESHOLD) {
            try { sessionStorage.removeItem('creditsBannerDismissed'); } catch (e) { console.warn('sessionStorage unavailable:', e); }
            setCreditsBannerDismissed(false);
            lastToastedRef.current = null;
          }
        })
        .catch(err => console.error('Error fetching credits:', err));
    }
  }, [companyId]);
  
  useEffect(() => {
    fetchCreditsBalance();
  }, [fetchCreditsBalance]);

  // Alertă sold credite scăzut (< 30): toast la prima detectare în sesiune.
  // Resetul flag-urilor (când balance trece peste prag) se face în fetchCreditsBalance.
  useEffect(() => {
    if (creditsBalance <= 0) return; // 0 înseamnă "încă nu s-a încărcat" sau cont nou
    if (creditsBalance >= LOW_CREDITS_THRESHOLD) return;
    // Sold sub prag — afișăm toast o singură dată per sesiune
    if (lastToastedRef.current === null) {
      lastToastedRef.current = creditsBalance;
      toast.warning(
        `⚠️ Sold credite scăzut: ${creditsBalance} cr. Alimentează contul ca să nu blochezi facturile/devizele.`,
        {
          duration: 8000,
          action: { label: 'Alimentează', onClick: () => setShowPlatiDialog(true) },
        }
      );
    }
  }, [creditsBalance]);

  const showLowCreditsBanner = creditsBalance > 0 && creditsBalance < LOW_CREDITS_THRESHOLD && !creditsBannerDismissed;
  const dismissLowCreditsBanner = () => {
    setCreditsBannerDismissed(true);
    try { sessionStorage.setItem('creditsBannerDismissed', '1'); } catch (e) { console.warn('sessionStorage unavailable:', e); }
  };
  
  // Set document title for Admin Dashboard
  useEffect(() => {
    const companyName = user.company_name || "Firmă";
    document.title = `e-Tesa - ${companyName} (Admin)`;
    return () => {
      document.title = "e-Tesa";
    };
  }, [user.company_name]);

  // Polling badge pentru job-uri în procesare (la 15s când panoul e închis)
  useEffect(() => {
    if (!companyId) return;
    if (showBackgroundJobs) return; // panoul are propriul polling la 5s
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/background-jobs/stats`, { params: { company_id: companyId } });
        if (!cancelled) setJobsProcessingCount(r?.data?.processing || 0);
      } catch { /* silent */ }
    };
    fetchCount();
    const interval = setInterval(fetchCount, 15000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [companyId, showBackgroundJobs]);

  // Handle browser back button - navigate to Acasă tab instead of logout
  useEffect(() => {
    const handleBackButton = () => {
      if (activeTab !== "dashboard") {
        setActiveTab("dashboard");
        window.history.pushState({ tab: "dashboard" }, "");
      } else {
        // Already on home, push state again to prevent leaving
        window.history.pushState({ tab: "dashboard" }, "");
      }
    };
    window.history.pushState({ tab: activeTab }, "");
    window.addEventListener("popstate", handleBackButton);
    return () => window.removeEventListener("popstate", handleBackButton);
  }, [activeTab]);

  // Push history state when tab changes
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    window.history.pushState({ tab: newTab }, "");
  };

  // Check if tutorial should be shown on first visit
  useEffect(() => {
    const tutorialCompleted = localStorage.getItem('etesa_tutorial_completed');
    const hiddenUntil = localStorage.getItem('etesa_tutorial_hidden_until');
    
    // Check if hidden temporarily (2 weeks)
    if (hiddenUntil) {
      const hideUntilTime = parseInt(hiddenUntil, 10);
      if (Date.now() < hideUntilTime) {
        // Still within the 2-week hide period
        return;
      } else {
        // 2 weeks passed, clear the flag
        localStorage.removeItem('etesa_tutorial_hidden_until');
      }
    }
    
    // Tutorialul este accesibil DOAR manual din butonul din header — nu se mai deschide automat
    // (cerere user: deranjant la fiecare deschidere)
  }, []);
  
  // Editare prezență de către admin
  const [editingAttendance, setEditingAttendance] = useState(null);
  const [editAttendanceData, setEditAttendanceData] = useState({
    site_id: "",
    tool_ids: [],
    uses_company_car: false,
    company_car_plate: "",
    check_in: "",
    check_out: ""
  });
  
  // Vehicles state
  const [vehicles, setVehicles] = useState([]);
  const [openVehicleDialog, setOpenVehicleDialog] = useState(false);
  const [newVehicle, setNewVehicle] = useState({ plate: "", name: "", model_year: "", vehicle_type: "Van", initial_km: "" });
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [editVehicleKm, setEditVehicleKm] = useState("");
  
  const [newTool, setNewTool] = useState({ name: "", description: "" });
  const [newWorker, setNewWorker] = useState({ name: "", email: "", phone: "", password: "", home_address: "" });
  const [newSubcontractor, setNewSubcontractor] = useState({ name: "", contact_person: "", phone: "", email: "" });
  const [newSubEmployee, setNewSubEmployee] = useState({ name: "", phone: "", specialization: "" });
  const [selectedSite, setSelectedSite] = useState(null);
  const [siteSettings, setSiteSettings] = useState({ work_start_time: "07:00", work_end_time: "16:00", lunch_break_start: "12:00", lunch_break_end: "13:00", assigned_team: [], default_tools: [], assigned_subcontractors: [], start_date: "", end_date: "", progress_percent: 0, total_value: 0, expenses: 0 });
  
  const [openToolDialog, setOpenToolDialog] = useState(false);
  const [openWorkerDialog, setOpenWorkerDialog] = useState(false);
  const [openSubcontractorDialog, setOpenSubcontractorDialog] = useState(false);
  const [openSubEmployeeDialog, setOpenSubEmployeeDialog] = useState(false);
  const [selectedSubcontractor, setSelectedSubcontractor] = useState(null);
  const [expandedSubcontractors, setExpandedSubcontractors] = useState([]);
  const [openSiteSettings, setOpenSiteSettings] = useState(false);
  const [showExpensesBreakdown, setShowExpensesBreakdown] = useState(false);
  const [expensesData, setExpensesData] = useState(null);
  const [newExpense, setNewExpense] = useState({ description: "", amount: 0, category: "general", date: "" });
  const [openProjectReport, setOpenProjectReport] = useState(false);
  const [selectedSiteForReport, setSelectedSiteForReport] = useState(null);
  const [holidaysExpanded, setHolidaysExpanded] = useState(false);

  // (fetchAll useEffect moved below after fetchAll definition)

  // Auto-refresh calendar data when month or year changes
  useEffect(() => {
    if (workers.length > 0) {
      fetchAllWorkersCalendarData(calendarMonth, calendarYear);
    }
  }, [calendarMonth, calendarYear, workers.length]);

  // Check for demo data when sites/tools change
  useEffect(() => {
    const hasDemo = 
      sites.some(s => s.is_demo === true) ||
      tools.some(t => t.is_demo === true) ||
      workers.some(w => w.is_demo === true);
    
    if (hasDemo) {
      const dismissed = localStorage.getItem('demoBannerDismissed');
      setHasDemoData(true);
      setShowDemoBanner(!dismissed);
    } else {
      setHasDemoData(false);
    }
  }, [sites, tools, workers]);

  // Filter contracts when a site is selected
  useEffect(() => {
    if (selectedSiteForContracts && documents) {
      const contracts = documents.filter(doc => 
        doc.site_id === selectedSiteForContracts.id ||
        (doc.document_type === 'contract_servicii' && doc.site_name === selectedSiteForContracts.name)
      );
      setSiteContracts(contracts);
    }
  }, [selectedSiteForContracts, documents]);

  const handleDeleteDemoData = async () => {
    setDeletingDemo(true);
    try {
      await axios.delete(`${API}/demo-data`);
      toast.success("Date demo șterse cu succes!");
      setHasDemoData(false);
      setShowDemoBanner(false);
      // Refresh all data
      fetchAll();
    } catch (error) {
      toast.error("Eroare la ștergerea datelor demo");
    } finally {
      setDeletingDemo(false);
    }
  };

  const fetchVehicles = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/vehicles`, { params: { company_id: companyId } });
      setVehicles(response.data);
    } catch (error) {
      console.error("Error fetching vehicles:", error);
    }
  }, [companyId]);

  const fetchDocuments = useCallback(async () => {
    try {
      const [docsRes, statsRes] = await Promise.all([
        axios.get(`${API}/documents`, { params: { company_id: companyId } }),
        axios.get(`${API}/documents/stats/summary`, { params: { company_id: companyId } })
      ]);
      setDocuments(docsRes.data);
      setDocumentStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching documents:", error);
    }
  }, [companyId]);

  const fetchTasks = useCallback(async () => {
    try {
      const [tasksRes, statsRes] = await Promise.all([
        axios.get(`${API}/tasks`, { params: { company_id: companyId } }),
        axios.get(`${API}/tasks/stats/summary`, { params: { company_id: companyId } })
      ]);
      setTasks(tasksRes.data);
      setTaskStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching tasks:", error);
    }
  }, [companyId]);

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/tasks`, {
        ...newTask,
        assigned_by: user.id,
        assigned_by_name: user.name
      }, { params: { company_id: companyId } });
      toast.success("Tichet creat și trimis echipei!");
      setNewTask({ title: "", description: "", site_id: "", location_details: "", priority: "normal", assigned_to: [] });
      setShowTaskDialog(false);
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la creare tichet");
    }
  };

  const handleCancelTask = async (taskId) => {
    try {
      await axios.post(`${API}/tasks/${taskId}/cancel`, {}, { params: { company_id: companyId } });
      toast.success("Tichet anulat");
      fetchTasks();
    } catch (error) {
      toast.error("Eroare la anulare");
    }
  };

  const fetchPendingLeaves = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/leaves/pending`, { params: { company_id: companyId } });
      setPendingLeaves(response.data);
    } catch (error) {
      console.error("Error fetching leaves:", error);
    }
  }, [companyId]);

  const fetchHolidays = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/holidays`);
      setHolidays(response.data);
    } catch (error) {
      console.error("Error fetching holidays:", error);
    }
  }, []);

  const fetchCalendarData = async (workerId, month, year) => {
    try {
      const response = await axios.get(`${API}/reports/monthly/${workerId}/${year}/${month}`);
      setCalendarData(response.data);
    } catch (error) {
      console.error("Error fetching calendar data:", error);
    }
  };

  const fetchAllWorkersCalendarData = async (month, year) => {
    setLoadingCalendar(true);
    try {
      // Fetch monthly data for all workers
      const promises = workers.map(worker => 
        axios.get(`${API}/reports/monthly/${worker.id}/${year}/${month}`)
          .then(res => ({ [worker.id]: { ...res.data, name: worker.name } }))
          .catch(err => ({ [worker.id]: { attendances: [], leaves: [], holidays: [], absences: [], overtime: [], name: worker.name } }))
      );
      
      const results = await Promise.all(promises);
      const combinedData = results.reduce((acc, curr) => ({ ...acc, ...curr }), {});
      setAllWorkersCalendarData(combinedData);
      
      // Fetch yearly leave summary
      try {
        const yearlyResponse = await axios.get(`${API}/leaves/yearly-summary/${year}`);
        setYearlyLeaveData(yearlyResponse.data);
      } catch (err) {
        console.error("Error fetching yearly leave data:", err);
        setYearlyLeaveData({});
      }
    } catch (error) {
      console.error("Error fetching all calendar data:", error);
    } finally {
      setLoadingCalendar(false);
    }
  };

  const fetchAutoAttendanceSettings = async () => {
    if (!companyId) return;
    setLoadingAutoPontaj(true);
    try {
      const res = await axios.get(`${API}/attendance/auto-settings?company_id=${companyId}`);
      setAutoAttendanceSettings(res.data);
    } catch (err) {
      console.error("Error fetching auto-attendance settings:", err);
    } finally {
      setLoadingAutoPontaj(false);
    }
  };

  const toggleAutoAttendance = async (userId, enabled) => {
    try {
      await axios.put(`${API}/attendance/auto-settings?company_id=${companyId}&user_id=${userId}&enabled=${enabled}`);
      toast.success(`Auto-pontaj ${enabled ? 'activat' : 'dezactivat'}`);
      fetchAutoAttendanceSettings();
    } catch (err) {
      toast.error("Eroare la actualizarea setărilor");
    }
  };

  const toggleBulkAutoAttendance = async (enabled) => {
    try {
      await axios.put(`${API}/attendance/auto-settings?company_id=${companyId}&bulk=true&enabled=${enabled}`);
      toast.success(`Auto-pontaj ${enabled ? 'activat' : 'dezactivat'} pentru toți angajații`);
      fetchAutoAttendanceSettings();
    } catch (err) {
      toast.error("Eroare la actualizarea setărilor");
    }
  };

  const fetchAdminSignatureStatus = async () => {
    if (!user?.id || !calendarMonth || !calendarYear) return;
    try {
      const res = await axios.get(`${API}/signatures/status/${user.id}/${calendarMonth}/${calendarYear}`);
      setAdminSignatureStatus(res.data);
    } catch (err) {
      console.error("Error fetching admin signature status:", err);
    }
  };

  useEffect(() => {
    if (user?.id && calendarMonth && calendarYear) {
      fetchAdminSignatureStatus();
    }
  }, [calendarMonth, calendarYear, user?.id]);

  const fetchSites = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/sites?company_id=${companyId}`);
      setSites(response.data);
    } catch (error) {
      console.error("Error fetching sites:", error);
    }
  }, [companyId]);

  const fetchDevize = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/devize?company_id=${companyId}`);
      setDevize(response.data);
    } catch (error) {
      console.error("Error fetching devize:", error);
    }
  }, [companyId]);

  const fetchTools = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/tools?company_id=${companyId}`);
      setTools(response.data);
    } catch (error) {
      console.error("Error fetching tools:", error);
    }
  }, [companyId]);

  const fetchWorkers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/users`, { params: { company_id: companyId } });
      setWorkers(response.data.filter(u => u.role === 'worker'));
    } catch (error) {
      console.error("Error fetching workers:", error);
    }
  }, [companyId]);

  const fetchSubcontractors = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/subcontractors`, { params: { company_id: companyId } });
      setSubcontractors(response.data);
    } catch (error) {
      console.error("Error fetching subcontractors:", error);
    }
  }, [companyId]);

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/notifications`, { params: { company_id: companyId } });
      setNotifications(response.data);
      
      // Get unread count
      const countResponse = await axios.get(`${API}/notifications/unread/count`, { params: { company_id: companyId } });
      setUnreadCount(countResponse.data.count);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  }, [companyId]);

  const fetchAll = useCallback(() => {
    fetchSites();
    fetchTools();
    fetchWorkers();
    fetchSubcontractors();
    fetchNotifications();
    fetchPendingLeaves();
    fetchHolidays();
    fetchDocuments();
    fetchTasks();
    fetchVehicles();
    fetchDevize();
  }, [fetchSites, fetchTools, fetchWorkers, fetchSubcontractors, fetchNotifications, fetchPendingLeaves, fetchHolidays, fetchDocuments, fetchTasks, fetchVehicles, fetchDevize]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleAddTool = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/tools`, newTool, { params: { company_id: companyId } });
      toast.success("Unealtă adăugată!");
      setNewTool({ name: "", description: "" });
      setOpenToolDialog(false);
      fetchTools();
    } catch (error) {
      toast.error("Eroare la adăugare unealtă");
    }
  };

  const handleAddWorker = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/auth/create-worker`, {
        name: newWorker.name,
        email: newWorker.email || null,
        phone: newWorker.phone || null,
        password: newWorker.password,
        home_address: newWorker.home_address || "",
        created_by: user.id
      }, { params: { company_id: companyId } });
      
      const loginInfo = response.data.login_info;
      toast.success(
        <div>
          <p><strong>Angajat creat!</strong></p>
          <p className="text-sm mt-1">Login: {loginInfo.email}</p>
          <p className="text-sm">Parolă: {loginInfo.password}</p>
        </div>,
        { duration: 10000 }
      );
      
      setNewWorker({ name: "", email: "", phone: "", password: "", home_address: "" });
      setOpenWorkerDialog(false);
      fetchWorkers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la adăugare angajat");
    }
  };

  const handleDeleteTool = async (id) => {
    try {
      await axios.delete(`${API}/tools/${id}`, { params: { company_id: companyId } });
      toast.success("Unealtă ștearsă!");
      fetchTools();
    } catch (error) {
      toast.error("Eroare la ștergere");
    }
  };

  // Vehicle handlers
  const handleAddVehicle = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/vehicles`, {
        plate: newVehicle.plate,
        name: newVehicle.name,
        model_year: newVehicle.model_year ? parseInt(newVehicle.model_year) : null,
        vehicle_type: newVehicle.vehicle_type,
        initial_km: parseFloat(newVehicle.initial_km) || 0
      }, { params: { company_id: companyId } });
      toast.success("Vehicul adăugat!");
      setNewVehicle({ plate: "", name: "", model_year: "", vehicle_type: "Van", initial_km: "" });
      setOpenVehicleDialog(false);
      fetchVehicles();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la adăugare vehicul");
    }
  };

  const handleDeleteVehicle = async (id) => {
    try {
      await axios.delete(`${API}/vehicles/${id}`, { params: { company_id: companyId } });
      toast.success("Vehicul șters!");
      setSelectedVehicle(null);
      fetchVehicles();
    } catch (error) {
      toast.error("Eroare la ștergere");
    }
  };

  const handleUpdateVehicleKm = async () => {
    if (!selectedVehicle || !editVehicleKm) return;
    try {
      await axios.put(`${API}/vehicles/${selectedVehicle.id}`, {
        current_km: parseFloat(editVehicleKm)
      }, { params: { company_id: companyId } });
      toast.success("Kilometraj actualizat!");
      setSelectedVehicle(null);
      setEditVehicleKm("");
      fetchVehicles();
    } catch (error) {
      toast.error("Eroare la actualizare");
    }
  };

  const handleDeleteWorker = async (id) => {
    try {
      await axios.delete(`${API}/users/${id}`, { params: { company_id: companyId } });
      toast.success("Angajat șters!");
      fetchWorkers();
    } catch (error) {
      toast.error("Eroare la ștergere");
    }
  };

  // Subcontractor handlers
  const handleAddSubcontractor = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/subcontractors`, newSubcontractor, { params: { company_id: companyId } });
      toast.success("Subcontractor adăugat!");
      setNewSubcontractor({ name: "", contact_person: "", phone: "", email: "" });
      setOpenSubcontractorDialog(false);
      fetchSubcontractors();
    } catch (error) {
      toast.error("Eroare la adăugare subcontractor");
    }
  };

  const handleDeleteSubcontractor = async (id) => {
    try {
      await axios.delete(`${API}/subcontractors/${id}`, { params: { company_id: companyId } });
      toast.success("Subcontractor șters!");
      fetchSubcontractors();
    } catch (error) {
      toast.error("Eroare la ștergere");
    }
  };

  const handleAddSubEmployee = async (e) => {
    e.preventDefault();
    if (!selectedSubcontractor) return;
    try {
      await axios.post(`${API}/subcontractors/${selectedSubcontractor.id}/employees`, newSubEmployee, { params: { company_id: companyId } });
      toast.success("Angajat adăugat la subcontractor!");
      setNewSubEmployee({ name: "", phone: "", specialization: "" });
      setOpenSubEmployeeDialog(false);
      fetchSubcontractors();
    } catch (error) {
      toast.error("Eroare la adăugare angajat");
    }
  };

  const handleDeleteSubEmployee = async (subcontractorId, employeeId) => {
    try {
      await axios.delete(`${API}/subcontractors/${subcontractorId}/employees/${employeeId}`, { params: { company_id: companyId } });
      toast.success("Angajat șters!");
      fetchSubcontractors();
    } catch (error) {
      toast.error("Eroare la ștergere");
    }
  };

  const toggleSubcontractorExpanded = (id) => {
    setExpandedSubcontractors(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  // Salvare editare prezență de către admin
  const handleSaveAttendanceEdit = async () => {
    if (!editingAttendance) return;
    try {
      // Construiește datetime complet
      const dateStr = editingAttendance.check_in.split('T')[0];
      const checkInISO = `${dateStr}T${editAttendanceData.check_in}:00Z`;
      const checkOutISO = editAttendanceData.check_out ? `${dateStr}T${editAttendanceData.check_out}:00Z` : null;
      
      await axios.put(`${API}/attendance/${editingAttendance.id}/admin-edit`, {
        attendance_id: editingAttendance.id,
        site_id: editAttendanceData.site_id,
        tool_ids: editAttendanceData.tool_ids,
        uses_company_car: editAttendanceData.uses_company_car,
        company_car_plate: editAttendanceData.company_car_plate,
        check_in: checkInISO,
        check_out: checkOutISO
      }, { params: { company_id: companyId } });
      
      toast.success("Prezență actualizată!");
      setEditingAttendance(null);
        fetchTools(); // Reîncarcă uneltele pentru a actualiza orele
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la actualizare");
    }
  };

  // Verifică disponibilitatea unei unelte
  const [toolAvailability, setToolAvailability] = useState({});
  
  const checkToolAvailability = async (toolId) => {
    if (!editingAttendance) return;
    try {
      const dateStr = editingAttendance.check_in.split('T')[0];
      const response = await axios.get(`${API}/tools/${toolId}/availability`, {
        params: { date: dateStr, company_id: companyId }
      });
      setToolAvailability(prev => ({...prev, [toolId]: response.data}));
    } catch (error) {
      console.error("Error checking tool availability:", error);
    }
  };

  // Verifică toate uneltele când se deschide dialogul de editare
  useEffect(() => {
    if (editingAttendance) {
      tools.forEach(tool => {
        checkToolAvailability(tool.id);
      });
    } else {
      setToolAvailability({});
    }
  }, [editingAttendance]);

  const handleOpenSiteSettings = async (site) => {
    setSelectedSite(site);
    setSiteSettings({
      work_start_time: site.work_start_time || "07:00",
      work_end_time: site.work_end_time || "16:00",
      lunch_break_start: site.lunch_break_start || "12:00",
      lunch_break_end: site.lunch_break_end || "13:00",
      assigned_team: site.assigned_team || [],
      default_tools: site.default_tools || [],
      assigned_subcontractors: site.assigned_subcontractors || [],
      start_date: site.start_date || "",
      end_date: site.end_date || "",
      progress_percent: site.progress_percent || 0,
      total_value: site.total_value || 0,
      expenses: site.expenses || 0
    });
    setOpenSiteSettings(true);
    
    // Încarcă breakdown-ul cheltuielilor
    try {
      const res = await axios.get(`${API}/sites/${site.id}/expenses?company_id=${companyId}`);
      setExpensesData(res.data);
    } catch (error) {
      console.error("Error loading expenses:", error);
      setExpensesData(null);
    }
  };

  const handleAddExpense = async () => {
    if (!newExpense.description || newExpense.amount <= 0) {
      toast.error("Completați descrierea și suma");
      return;
    }
    
    try {
      await axios.post(
        `${API}/sites/${selectedSite.id}/expenses?company_id=${companyId}&description=${encodeURIComponent(newExpense.description)}&amount=${newExpense.amount}&category=${newExpense.category}&date=${newExpense.date || ''}`
      );
      toast.success("Cheltuială adăugată!");
      setNewExpense({ description: "", amount: 0, category: "general", date: "" });
      setShowExpensesBreakdown(false);
      
      // Reîncarcă datele
      const res = await axios.get(`${API}/sites/${selectedSite.id}/expenses?company_id=${companyId}`);
      setExpensesData(res.data);
      setSiteSettings(prev => ({ ...prev, expenses: res.data.total_expenses }));
    } catch (error) {
      toast.error("Eroare la adăugare cheltuială");
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    try {
      await axios.delete(`${API}/sites/${selectedSite.id}/expenses/${expenseId}?company_id=${companyId}`);
      toast.success("Cheltuială ștearsă!");
      
      // Reîncarcă datele
      const res = await axios.get(`${API}/sites/${selectedSite.id}/expenses?company_id=${companyId}`);
      setExpensesData(res.data);
      setSiteSettings(prev => ({ ...prev, expenses: res.data.total_expenses }));
    } catch (error) {
      toast.error("Eroare la ștergere cheltuială");
    }
  };

  const handleSaveSiteSettings = async () => {
    try {
      await axios.put(`${API}/sites/${selectedSite.id}?company_id=${companyId}`, siteSettings);
      toast.success("Setări actualizate!");
      setOpenSiteSettings(false);
      fetchSites();
    } catch (error) {
      toast.error("Eroare la actualizare setări");
    }
  };

  const handleApproveLeave = async (leaveId, approved) => {
    try {
      await axios.post(`${API}/leaves/approve`, {
        leave_id: leaveId,
        approved,
        approved_by: user.name
      });
      toast.success(approved ? "Concediu aprobat! Document adăugat în registru." : "Concediu respins!");
      fetchPendingLeaves();
      fetchNotifications();
      if (approved) {
        fetchDocuments();  // Reload documents when leave is approved
      }
    } catch (error) {
      toast.error("Eroare la procesare cerere");
    }
  };

  const handleDownloadLeaveTemplate = async (leaveId) => {
    try {
      const response = await axios.get(`${API}/leaves/${leaveId}/template`);
      const { html_template } = response.data;
      
      // Create a new window with the HTML template
      const printWindow = safeOpenHTMLWindow(html_template);
      if (!printWindow) return;
      
      // Add print button
      const printBtn = printWindow.document.createElement('button');
      printBtn.textContent = '🖨️ Printează / Salvează PDF';
      printBtn.style.cssText = 'position: fixed; top: 10px; right: 10px; padding: 10px 20px; background: #0d9488; color: white; border: none; border-radius: 8px; cursor: pointer; font-weight: bold; z-index: 9999;';
      printBtn.onclick = () => printWindow.print();
      printWindow.document.body.appendChild(printBtn);
      
      toast.success("Cerere deschisă în fereastră nouă - poți printa sau salva ca PDF");
    } catch (error) {
      toast.error("Eroare la generare cerere");
    }
  };

  const handleAddHoliday = async (date, name) => {
    try {
      await axios.post(`${API}/holidays`, {
        date: new Date(date).toISOString(),
        name,
        type: "legal"
      });
      toast.success("Zi liberă adăugată!");
      fetchHolidays();
    } catch (error) {
      toast.error("Eroare la adăugare zi liberă");
    }
  };

  const handleDeleteHoliday = async (holidayId) => {
    try {
      await axios.delete(`${API}/holidays/${holidayId}`);
      toast.success("Zi liberă ștearsă!");
      fetchHolidays();
    } catch (error) {
      toast.error("Eroare la ștergere");
    }
  };

  const handleTeamToggle = (workerId) => {
    setSiteSettings(prev => ({
      ...prev,
      assigned_team: prev.assigned_team.includes(workerId)
        ? prev.assigned_team.filter(id => id !== workerId)
        : [...prev.assigned_team, workerId]
    }));
  };

  const handleLogout = () => {
    // If superadmin is managing a company, "Ieșire" should return to the
    // superadmin dashboard instead of logging out completely.
    const managingRaw = localStorage.getItem('superadmin_managing');
    if (managingRaw) {
      try {
        const managing = JSON.parse(managingRaw);
        if (managing?.original_user) {
          const storageTarget = managing.original_storage === 'session' ? sessionStorage : localStorage;
          const otherStorage = managing.original_storage === 'session' ? localStorage : sessionStorage;
          storageTarget.setItem('user', JSON.stringify(managing.original_user));
          otherStorage.removeItem('user');
          localStorage.setItem('company_id', managing.original_user.company_id || '');
          localStorage.setItem('company_name', managing.original_user.company_name || '');
        }
      } catch (e) { console.warn('Failed to parse superadmin_managing payload:', e); }
      localStorage.removeItem('superadmin_managing');
      navigate('/superadmin');
      return;
    }
    localStorage.removeItem('user');
    sessionStorage.removeItem('user');
    localStorage.removeItem('rememberMe');
    navigate('/login');
  };

  const formatTime = (dateString) => {
    if (!dateString) return "-";
    // Data este stocată ca ora locală dar cu +00:00, deci extragem doar HH:MM
    if (dateString.includes('T')) {
      const timePart = dateString.split('T')[1];
      if (timePart) {
        return timePart.substring(0, 5);
      }
    }
    const date = new Date(dateString);
    return date.toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Bucharest' });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return formatDateTimeRO(dateString);
  };

  // Delete attendance
  const handleDeleteAttendance = async (attendanceId) => {
    if (!confirm("Sigur doriți să ștergeți această prezență? Angajatul va fi considerat absent.")) return;
    try {
      await axios.delete(`${API}/attendance/${attendanceId}`);
      toast.success("Prezență ștearsă!");
    } catch (error) {
      toast.error("Eroare la ștergere: " + (error.response?.data?.detail || error.message));
    }
  };

  // Check if superadmin is managing this company
  const isSuperadminManaging = user?.isSuperadminManaging || false;
  const superadminData = isSuperadminManaging ? JSON.parse(localStorage.getItem('superadmin_managing') || '{}') : null;

  const handleReturnToSuperadmin = () => {
    // Restore original superadmin user to the same storage that was used at login
    if (superadminData?.original_user) {
      const storageTarget = superadminData.original_storage === 'session' ? sessionStorage : localStorage;
      const otherStorage = superadminData.original_storage === 'session' ? localStorage : sessionStorage;
      storageTarget.setItem('user', JSON.stringify(superadminData.original_user));
      otherStorage.removeItem('user');
      localStorage.setItem('company_id', superadminData.original_user.company_id || '');
      localStorage.setItem('company_name', superadminData.original_user.company_name || '');
    }
    localStorage.removeItem('superadmin_managing');
    navigate('/superadmin');
  };

  return (
    <GlassLayout data-testid="master-dashboard">
      {/* Superadmin Managing Banner */}
      {isSuperadminManaging && (
        <div className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white py-2 px-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Crown className="w-5 h-5" />
            <span className="font-medium">
              Mod Superadmin - Gestionezi firma: <strong>{superadminData?.company_name}</strong>
            </span>
          </div>
          <Button 
            onClick={handleReturnToSuperadmin}
            size="sm"
            variant="secondary"
            className="bg-white text-orange-600 hover:bg-orange-100"
          >
            ← Înapoi la Superadmin
          </Button>
        </div>
      )}
      
      {/* Header */}
      <div className="glass-header sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-blue-600 rounded-xl flex items-center justify-center">
              <LayoutDashboard className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold" style={{fontFamily: 'Space Grotesk, sans-serif'}}>e-Tesa</h1>
              <p className="text-xs sm:text-sm text-slate-600">{user.name} • {isSuperadminManaging ? 'Superadmin' : 'Admin'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Credits Balance Indicator + Plati Button */}
            <div 
              className={`flex items-center gap-1 px-3 py-1.5 rounded-full cursor-pointer transition-colors border ${
                creditsBalance > 0 && creditsBalance < LOW_CREDITS_THRESHOLD
                  ? 'bg-gradient-to-r from-red-100 to-orange-100 border-red-300 hover:from-red-200 hover:to-orange-200 animate-pulse'
                  : 'bg-gradient-to-r from-yellow-100 to-orange-100 border-yellow-300 hover:from-yellow-200 hover:to-orange-200'
              }`}
              onClick={() => setShowPlatiDialog(true)}
              title={creditsBalance < LOW_CREDITS_THRESHOLD ? "Sold scăzut — apasă pentru alimentare" : "Sold Credite - Click pentru meniul Plăți"}
              data-testid="credits-balance-indicator"
            >
              <Coins className={`w-4 h-4 ${creditsBalance < LOW_CREDITS_THRESHOLD ? 'text-red-600' : 'text-yellow-600'}`} />
              <span className={`text-sm font-semibold ${creditsBalance < LOW_CREDITS_THRESHOLD ? 'text-red-700' : 'text-yellow-700'}`}>{creditsBalance.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              <span className={`text-xs hidden sm:inline ml-1 ${creditsBalance < LOW_CREDITS_THRESHOLD ? 'text-red-600' : 'text-yellow-600'}`}>{creditsBalance < LOW_CREDITS_THRESHOLD ? 'Alimentează' : 'Plăți'}</span>
            </div>
            <div className="relative">
              <Bell className="w-5 h-5 text-slate-600" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </div>
            <Button 
              onClick={() => setShowTutorial(true)} 
              variant="ghost" 
              size="sm"
              className="rounded-full p-2 hover:bg-teal-100"
              data-testid="tutorial-button"
              title="Tutorial"
            >
              <GraduationCap className="w-5 h-5 text-teal-600" />
            </Button>
            <Button 
              onClick={() => setShowSettings(true)} 
              variant="ghost" 
              size="sm"
              className="rounded-full p-2"
              data-testid="settings-button"
              title="Setări Cont"
            >
              <Settings className="w-5 h-5 text-slate-600" />
            </Button>
            <Button
              onClick={() => setShowBackgroundJobs(true)}
              variant="ghost"
              size="sm"
              className="rounded-full p-2 relative"
              data-testid="background-jobs-button"
              title="Jobs în procesare"
            >
              <Activity className={`w-5 h-5 ${jobsProcessingCount > 0 ? "text-blue-600 animate-pulse" : "text-slate-600"}`} />
              {jobsProcessingCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-blue-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1" data-testid="jobs-badge">
                  {jobsProcessingCount > 9 ? "9+" : jobsProcessingCount}
                </span>
              )}
            </Button>
            <Button 
              onClick={() => setShowCompanySettings(true)} 
              variant="ghost" 
              size="sm"
              className="rounded-full p-2"
              data-testid="company-settings-button"
              title="Setări Firmă"
            >
              <Building2 className="w-5 h-5 text-purple-600" />
            </Button>
            <Button onClick={handleLogout} variant="outline" className="rounded-full px-3 sm:px-4" data-testid="logout-button">
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Ieșire</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Banner Sold Credite Scăzut — apare sub header când balance < 30 și user nu l-a închis */}
      {showLowCreditsBanner && (
        <div
          className="sticky top-[64px] z-40 bg-gradient-to-r from-red-500 via-orange-500 to-amber-500 text-white shadow-md"
          data-testid="low-credits-banner"
        >
          <div className="container mx-auto px-4 py-2.5 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => setShowPlatiDialog(true)}
              className="flex items-center gap-2 flex-1 text-left hover:opacity-90 transition-opacity"
              data-testid="low-credits-banner-cta"
              title="Apasă pentru a alimenta contul"
            >
              <AlertTriangle className="w-5 h-5 flex-shrink-0 animate-pulse" />
              <span className="text-sm font-semibold">
                Sold credite scăzut: <strong>{creditsBalance} cr</strong> (sub pragul de {LOW_CREDITS_THRESHOLD}). Apasă aici pentru a alimenta contul.
              </span>
              <ExternalLink className="w-4 h-4 ml-1 hidden sm:inline" />
            </button>
            <button
              type="button"
              onClick={dismissLowCreditsBanner}
              className="text-white/80 hover:text-white text-xs font-medium px-2 py-1 rounded hover:bg-white/10 transition"
              data-testid="low-credits-banner-dismiss"
              title="Ascunde temporar"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Tutorial Overlay */}
      <TutorialOverlay 
        isOpen={showTutorial}
        onClose={() => setShowTutorial(false)}
        onComplete={() => {
          toast.success("Tutorial completat! Acum ești pregătit să folosești e-Tesa.");
        }}
      />

      {/* Background Jobs Panel */}
      <BackgroundJobsPanel
        open={showBackgroundJobs}
        onOpenChange={setShowBackgroundJobs}
        companyId={companyId}
      />

      {/* Settings Dialog */}
      <SettingsDialog 
        open={showSettings} 
        onOpenChange={setShowSettings}
        user={user}
        onUserUpdate={(updatedUser) => {
          // Refresh user data if needed
          window.location.reload();
        }}
      />

      {/* Company Settings Dialog */}
      <CompanySettingsDialog
        open={showCompanySettings}
        onOpenChange={setShowCompanySettings}
        companyId={companyId}
        onSave={(data) => {
          toast.success("Date firmă salvate!");
        }}
      />

      {/* Demo Data Banner */}
      {hasDemoData && showDemoBanner && (
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white py-3 px-4 shadow-lg">
          <div className="container mx-auto flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <span className="text-2xl">🎉</span>
              <div>
                <p className="font-semibold">Bine ați venit în e-Tesa!</p>
                <p className="text-sm text-amber-100">Am adăugat date demonstrative pentru a vă familiariza cu sistemul. Le puteți șterge oricând.</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                onClick={handleDeleteDemoData}
                disabled={deletingDemo}
                className="bg-white text-amber-600 hover:bg-amber-50 font-semibold"
                data-testid="delete-demo-button"
              >
                {deletingDemo ? (
                  <>
                    <Clock className="w-4 h-4 mr-2 animate-spin" />
                    Se șterge...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Șterge Date Demo
                  </>
                )}
              </Button>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => {
                  setShowDemoBanner(false);
                  localStorage.setItem('demoBannerDismissed', 'true');
                }}
                className="text-white hover:bg-amber-600"
              >
                <XCircle className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-8">
        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
          {/* Meniu Navigare pe 2 Rânduri - Centrat */}
          <TabsList className="bg-gradient-to-r from-slate-50 to-blue-50 backdrop-blur-sm shadow-lg border border-slate-200 p-3 sm:p-4 h-auto rounded-2xl w-full flex flex-col items-center gap-2">
            {/* Rândul 1: Acasă, Notificări, Inventar, Unelte */}
            <div className="flex justify-center gap-2 sm:gap-3 w-full pb-2 border-b border-slate-200">
              <TabsTrigger value="dashboard" className="flex-1 max-w-[100px] sm:max-w-[120px] rounded-xl px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-medium transition-all hover:scale-105 hover:bg-slate-100 data-[state=active]:bg-gradient-to-r data-[state=active]:from-teal-500 data-[state=active]:to-blue-500 data-[state=active]:text-white data-[state=active]:shadow-md flex flex-col sm:flex-row items-center justify-center gap-1" data-testid="dashboard-tab">
                <LayoutDashboard className="w-4 h-4" />
                <span className="text-[10px] sm:text-sm">Acasă</span>
              </TabsTrigger>
              <TabsTrigger value="notifications" className="flex-1 max-w-[100px] sm:max-w-[120px] rounded-xl px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-medium transition-all hover:scale-105 hover:bg-slate-100 data-[state=active]:bg-gradient-to-r data-[state=active]:from-teal-500 data-[state=active]:to-blue-500 data-[state=active]:text-white data-[state=active]:shadow-md flex flex-col sm:flex-row items-center justify-center gap-1 relative" data-testid="notifications-tab">
                <Bell className="w-4 h-4" />
                <span className="text-[10px] sm:text-sm">Notificări</span>
                {unreadCount > 0 && (
                  <Badge className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] px-1.5 py-0.5 min-w-[18px] h-[18px] flex items-center justify-center">{unreadCount}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="inventar" className="flex-1 max-w-[100px] sm:max-w-[120px] rounded-xl px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-medium transition-all hover:scale-105 hover:bg-slate-100 data-[state=active]:bg-gradient-to-r data-[state=active]:from-teal-500 data-[state=active]:to-blue-500 data-[state=active]:text-white data-[state=active]:shadow-md flex flex-col sm:flex-row items-center justify-center gap-1" data-testid="inventar-tab">
                <Package className="w-4 h-4" />
                <span className="text-[10px] sm:text-sm">Inventar</span>
              </TabsTrigger>
              <TabsTrigger value="tools" className="flex-1 max-w-[100px] sm:max-w-[120px] rounded-xl px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-medium transition-all hover:scale-105 hover:bg-slate-100 data-[state=active]:bg-gradient-to-r data-[state=active]:from-teal-500 data-[state=active]:to-blue-500 data-[state=active]:text-white data-[state=active]:shadow-md flex flex-col sm:flex-row items-center justify-center gap-1" data-testid="tools-tab">
                <Wrench className="w-4 h-4" />
                <span className="text-[10px] sm:text-sm">Unelte</span>
              </TabsTrigger>
            </div>
            
            {/* Rândul 2: Angajați, Pontaj, Foaie Parcurs, Sarcini */}
            <div className="flex justify-center gap-2 sm:gap-3 w-full pb-2 border-b border-slate-200">
              <TabsTrigger value="workers" className="flex-1 max-w-[100px] sm:max-w-[120px] rounded-xl px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-medium transition-all hover:scale-105 hover:bg-slate-100 data-[state=active]:bg-gradient-to-r data-[state=active]:from-teal-500 data-[state=active]:to-blue-500 data-[state=active]:text-white data-[state=active]:shadow-md flex flex-col sm:flex-row items-center justify-center gap-1" data-testid="workers-tab">
                <Users className="w-4 h-4" />
                <span className="text-[10px] sm:text-sm">Angajați</span>
              </TabsTrigger>
              <TabsTrigger value="leaves" className="flex-1 max-w-[100px] sm:max-w-[120px] rounded-xl px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-medium transition-all hover:scale-105 hover:bg-slate-100 data-[state=active]:bg-gradient-to-r data-[state=active]:from-teal-500 data-[state=active]:to-blue-500 data-[state=active]:text-white data-[state=active]:shadow-md flex flex-col sm:flex-row items-center justify-center gap-1" data-testid="leaves-tab">
                <CalendarIcon className="w-4 h-4" />
                <span className="text-[10px] sm:text-sm">Pontaj</span>
              </TabsTrigger>
              <TabsTrigger value="trips" className="flex-1 max-w-[100px] sm:max-w-[120px] rounded-xl px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-medium transition-all hover:scale-105 hover:bg-slate-100 data-[state=active]:bg-gradient-to-r data-[state=active]:from-teal-500 data-[state=active]:to-blue-500 data-[state=active]:text-white data-[state=active]:shadow-md flex flex-col sm:flex-row items-center justify-center gap-1" data-testid="trips-tab">
                <Car className="w-4 h-4" />
                <span className="text-[10px] sm:text-sm">F.Parcurs</span>
              </TabsTrigger>
              <TabsTrigger value="tasks" className="flex-1 max-w-[100px] sm:max-w-[120px] rounded-xl px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-medium transition-all hover:scale-105 hover:bg-slate-100 data-[state=active]:bg-gradient-to-r data-[state=active]:from-teal-500 data-[state=active]:to-blue-500 data-[state=active]:text-white data-[state=active]:shadow-md flex flex-col sm:flex-row items-center justify-center gap-1" data-testid="tasks-tab">
                <ClipboardList className="w-4 h-4" />
                <span className="text-[10px] sm:text-sm">Sarcini</span>
              </TabsTrigger>
            </div>
            
            {/* Rândul 3: Deviz, Documente, Proiecte, Facturi */}
            <div className="flex justify-center gap-2 sm:gap-3 w-full">
              <TabsTrigger value="deviz" className="flex-1 max-w-[100px] sm:max-w-[120px] rounded-xl px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-medium transition-all hover:scale-105 hover:bg-slate-100 data-[state=active]:bg-gradient-to-r data-[state=active]:from-teal-500 data-[state=active]:to-blue-500 data-[state=active]:text-white data-[state=active]:shadow-md flex flex-col sm:flex-row items-center justify-center gap-1" data-testid="deviz-tab">
                <Calculator className="w-4 h-4" />
                <span className="text-[10px] sm:text-sm">Deviz</span>
              </TabsTrigger>
              <TabsTrigger value="documents" className="flex-1 max-w-[100px] sm:max-w-[120px] rounded-xl px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-medium transition-all hover:scale-105 hover:bg-slate-100 data-[state=active]:bg-gradient-to-r data-[state=active]:from-teal-500 data-[state=active]:to-blue-500 data-[state=active]:text-white data-[state=active]:shadow-md flex flex-col sm:flex-row items-center justify-center gap-1" data-testid="documents-tab">
                <FileText className="w-4 h-4" />
                <span className="text-[10px] sm:text-sm">Documente</span>
              </TabsTrigger>
              <TabsTrigger value="sites" className="flex-1 max-w-[100px] sm:max-w-[120px] rounded-xl px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-medium transition-all hover:scale-105 hover:bg-slate-100 data-[state=active]:bg-gradient-to-r data-[state=active]:from-teal-500 data-[state=active]:to-blue-500 data-[state=active]:text-white data-[state=active]:shadow-md flex flex-col sm:flex-row items-center justify-center gap-1" data-testid="sites-tab">
                <MapPin className="w-4 h-4" />
                <span className="text-[10px] sm:text-sm">PROIECTE</span>
              </TabsTrigger>
              <TabsTrigger value="facturi" className="flex-1 max-w-[100px] sm:max-w-[120px] rounded-xl px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-medium transition-all hover:scale-105 hover:bg-slate-100 data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-500 data-[state=active]:to-pink-500 data-[state=active]:text-white data-[state=active]:shadow-md flex flex-col sm:flex-row items-center justify-center gap-1" data-testid="facturi-tab">
                <Receipt className="w-4 h-4" />
                <span className="text-[10px] sm:text-sm">Facturi</span>
              </TabsTrigger>
            </div>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="glass-tab-content space-y-6">
            <HomeTab 
              companyId={companyId} 
              sites={sites} 
              tools={tools} 
              workers={workers} 
              onEditAttendance={(att) => {
                setEditingAttendance(att);
                setEditAttendanceData({
                  site_id: att.site_id || "",
                  tool_ids: att.tool_ids || [],
                  uses_company_car: att.uses_company_car || false,
                  company_car_plate: att.company_car_plate || "",
                  check_in: att.check_in?.split('T')[1]?.substring(0, 5) || "07:00",
                  check_out: att.check_out?.split('T')[1]?.substring(0, 5) || ""
                });
              }}
              onDeleteAttendance={handleDeleteAttendance}
            />
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications" className="glass-tab-content space-y-6">
            <NotificationsTab companyId={companyId} userId={user.id} userName={user.name} onUnreadCountChange={setUnreadCount} />
          </TabsContent>

          {/* PROIECTE Tab with Phases support */}
          <TabsContent value="sites" className="glass-tab-content space-y-6">
            {selectedSiteForPhases ? (
              <ProjectPhasesTab
                site={selectedSiteForPhases}
                companyId={companyId}
                subcontractors={subcontractors}
                users={workers}
                devize={devize}
                onBack={() => setSelectedSiteForPhases(null)}
              />
            ) : (
              <SitesTab 
                companyId={companyId} 
                user={user}
                onOpenSiteSettings={handleOpenSiteSettings}
                onOpenReport={(site) => { setSelectedSiteForReport(site); setOpenProjectReport(true); }}
                onOpenContracts={setSelectedSiteForContracts}
                onOpenPhases={(site) => setSelectedSiteForPhases(site)}
              />
            )}
          </TabsContent>

          {/* Tools Tab */}
          <TabsContent value="tools" className="glass-tab-content space-y-6">
            <ToolsTab 
              tools={tools} 
              vehicles={vehicles} 
              onRefresh={() => { fetchTools(); fetchVehicles(); }}
              companyId={companyId}
            />
          </TabsContent>

          {/* Workers Tab */}
          <TabsContent value="workers" className="glass-tab-content space-y-6">
            <WorkersTab 
              workers={workers} 
              subcontractors={subcontractors} 
              onRefresh={() => { fetchWorkers(); fetchSubcontractors(); }}
              companyId={companyId}
              userId={user.id}
              documents={documents}
            />
          </TabsContent>

          <TabsContent value="leaves" className="glass-tab-content space-y-6">
            {/* Cereri Concediu - mutat în TOP */}
            <LeavesTab 
              leaves={pendingLeaves} 
              holidays={holidays}
              yearlyLeaveData={yearlyLeaveData}
              onRefresh={() => { fetchPendingLeaves(); fetchHolidays(); }} 
            />

            {/* Calendar Grid pentru Pontaj */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <CardTitle className="flex items-center gap-2" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                      Calendar Pontaj
                      <HelpTooltip section="pontaj" />
                    </CardTitle>
                    <CardDescription>Vizualizează și editează pontajul angajaților</CardDescription>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Select value={calendarMonth.toString()} onValueChange={(v) => {
                      setCalendarMonth(parseInt(v));
                      fetchAllWorkersCalendarData(parseInt(v), calendarYear);
                    }}>
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                          <SelectItem key={m} value={m.toString()}>
                            {new Date(2024, m-1).toLocaleString('ro-RO', {month: 'long'})}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={calendarYear.toString()} onValueChange={(v) => {
                      setCalendarYear(parseInt(v));
                      fetchAllWorkersCalendarData(calendarMonth, parseInt(v));
                    }}>
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[2024, 2025, 2026].map(y => (
                          <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button 
                      variant="outline" 
                      onClick={() => fetchAllWorkersCalendarData(calendarMonth, calendarYear)}
                      disabled={loadingCalendar}
                    >
                      {loadingCalendar ? "Se încarcă..." : "Actualizează"}
                    </Button>
                    <Button 
                      variant={showAutoPontaj ? "default" : "outline"}
                      data-testid="auto-pontaj-toggle-btn"
                      onClick={() => {
                        setShowAutoPontaj(!showAutoPontaj);
                        if (!autoAttendanceSettings) fetchAutoAttendanceSettings();
                      }}
                      className={showAutoPontaj ? "bg-blue-600 hover:bg-blue-700 text-white" : ""}
                    >
                      <Settings className="w-4 h-4 mr-1" />
                      Auto-Pontaj
                    </Button>
                    <Button
                      data-testid="admin-sign-pontaj-btn"
                      onClick={() => setShowAdminSignature(true)}
                      className={adminSignatureStatus?.has_signature 
                        ? "bg-amber-500 hover:bg-amber-600 text-white" 
                        : "bg-emerald-600 hover:bg-emerald-700 text-white"}
                    >
                      <PenTool className="w-4 h-4 mr-1" />
                      {adminSignatureStatus?.has_signature ? "Resemnează" : "Semnează Pontaj"}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              
              {/* Auto-Pontaj Settings Panel */}
              {showAutoPontaj && (
                <div className="px-6 pb-4" data-testid="auto-pontaj-panel">
                  <Card className="border border-blue-200 bg-blue-50/50">
                    <CardHeader className="pb-3 pt-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="text-base font-semibold flex items-center gap-2">
                            <Clock className="w-4 h-4 text-blue-600" />
                            Setări Pontaj Automat
                          </CardTitle>
                          <CardDescription className="text-xs mt-1">
                            Pontajul automat înregistrează prezența la ora de start a proiectului (sau 07:00-15:00 implicit) și checkout la finalul programului.
                            Weekend-urile sunt excluse. Intervenția manuală a angajatului are prioritate.
                          </CardDescription>
                        </div>
                        {autoAttendanceSettings && (
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-medium text-gray-700">Toți angajații</span>
                            <Switch
                              data-testid="auto-pontaj-bulk-switch"
                              checked={autoAttendanceSettings.company_enabled}
                              onCheckedChange={(checked) => toggleBulkAutoAttendance(checked)}
                            />
                          </div>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      {loadingAutoPontaj ? (
                        <p className="text-sm text-gray-500">Se încarcă...</p>
                      ) : autoAttendanceSettings?.employees?.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                          {autoAttendanceSettings.employees.map(emp => (
                            <div key={emp.user_id} className="flex items-center justify-between p-2 rounded-lg bg-white border border-gray-100 hover:border-blue-200 transition-colors">
                              <div className="flex items-center gap-2 min-w-0">
                                <div className={`w-2 h-2 rounded-full ${emp.auto_attendance_enabled ? 'bg-green-500' : 'bg-gray-300'}`} />
                                <span className="text-sm font-medium truncate">{emp.name}</span>
                              </div>
                              <Switch
                                data-testid={`auto-pontaj-switch-${emp.user_id}`}
                                checked={emp.auto_attendance_enabled}
                                onCheckedChange={(checked) => toggleAutoAttendance(emp.user_id, checked)}
                              />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">Niciun angajat găsit.</p>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}

              <CardContent>
              <AttendanceCalendarGrid 
                  workers={workers.filter(w => w.role === 'worker')}
                  sites={sites}
                  month={calendarMonth}
                  year={calendarYear}
                  allData={allWorkersCalendarData}
                  holidays={holidays}
                  yearlyLeaveData={yearlyLeaveData}
                  companyId={companyId}
                  companyName={user.company_name || user.name || ''}
                  adminUserId={user?.id}
                  onRefresh={() => fetchAllWorkersCalendarData(calendarMonth, calendarYear)}
                />
              </CardContent>
            </Card>
            
            {/* Stat de Plată - integrat în Pontaj */}
            <StatDePlataTab 
              workers={workers.filter(w => w.role === 'worker')}
              month={calendarMonth}
              year={calendarYear}
              allData={allWorkersCalendarData}
              companyId={companyId}
              companyName={user.company_name}
              onCreditsChange={fetchCreditsBalance}
            />
            
            {/* Admin Signature Pad */}
            <SignaturePad
              open={showAdminSignature}
              onOpenChange={setShowAdminSignature}
              userId={user?.id}
              userName={user?.name || user?.email}
              companyId={companyId}
              month={calendarMonth}
              year={calendarYear}
              onSignatureComplete={() => {
                fetchAdminSignatureStatus();
              }}
            />
          </TabsContent>

          <TabsContent value="trips" className="glass-tab-content">
            <TripSheetsPanel />
          </TabsContent>

          {/* Tasks/Tickets Tab */}
          <TabsContent value="tasks" className="glass-tab-content space-y-6">
            <TasksTab 
              tasks={tasks} 
              taskStats={taskStats}
              sites={sites}
              workers={workers} 
              onRefresh={() => { fetchTasks(); }}
              companyId={companyId}
            />
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents" className="glass-tab-content space-y-6">
            <DocumentsTab 
              documents={documents} 
              documentStats={documentStats}
              onRefresh={fetchDocuments}
              companyId={companyId}
              workers={workers}
              sites={sites}
              onNavigateToDeviz={handleNavigateToDeviz}
            />
          </TabsContent>

          {/* Deviz Tab — with DevizePRO sub-menu */}
          <TabsContent value="deviz" className="glass-tab-content space-y-4">
            <Tabs defaultValue="devize-standard" className="space-y-4">
              <TabsList className="bg-white/70 backdrop-blur-sm border border-slate-200 p-1 rounded-xl">
                <TabsTrigger
                  value="devize-standard"
                  className="rounded-lg px-4 py-2 text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm"
                  data-testid="devize-standard-subtab"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Devize
                </TabsTrigger>
                <TabsTrigger
                  value="devize-pro"
                  className="rounded-lg px-4 py-2 text-sm data-[state=active]:bg-gradient-to-r data-[state=active]:from-amber-400 data-[state=active]:to-orange-500 data-[state=active]:text-white data-[state=active]:shadow-md"
                  data-testid="devize-pro-subtab"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  DevizePRO
                  <Badge className="ml-1.5 bg-white/20 text-[9px] font-bold border-0 px-1.5">AI</Badge>
                </TabsTrigger>
                <TabsTrigger
                  value="schite"
                  className="rounded-lg px-4 py-2 text-sm data-[state=active]:bg-gradient-to-r data-[state=active]:from-teal-500 data-[state=active]:to-cyan-500 data-[state=active]:text-white data-[state=active]:shadow-md"
                  data-testid="schite-subtab"
                >
                  <Pencil className="w-4 h-4 mr-2" />
                  Schițe
                </TabsTrigger>
              </TabsList>
              <TabsContent value="devize-standard">
                <DevizTab sites={sites} companyId={companyId} initialEditDevizId={pendingEditDevizId} editDevizNonce={editDevizNonce} onDevizOpened={() => setPendingEditDevizId(null)} />
              </TabsContent>
              <TabsContent value="devize-pro">
                <DevizProTab companyId={companyId} onDevizCreated={() => {}} />
              </TabsContent>
              <TabsContent value="schite">
                <SketchesTab companyId={companyId} />
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* Inventar Tab */}
          <TabsContent value="inventar" className="glass-tab-content space-y-6">
            <InventarTab companyId={companyId} />
          </TabsContent>

          {/* Facturi Tab */}
          <TabsContent value="facturi" className="glass-tab-content space-y-6">
            <FacturiTab companyId={companyId} sites={sites} devize={devize} />
          </TabsContent>

        </Tabs>

      {/* Plăți Dialog */}
      <Dialog open={showPlatiDialog} onOpenChange={setShowPlatiDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
          <PlatiTab companyId={companyId} userData={user} onBalanceChange={fetchCreditsBalance} />
        </DialogContent>
      </Dialog>
      </div>

      {/* Site Settings Dialog */}
      <Dialog open={openSiteSettings} onOpenChange={setOpenSiteSettings}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              Setări Proiect: {selectedSite?.name}
            </DialogTitle>
            <DialogDescription>Configurează proiectul, echipa și aspectele financiare</DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            {/* Project Timeline Section */}
            <div className="bg-teal-50 p-4 rounded-lg border border-teal-200">
              <h4 className="font-semibold mb-3 flex items-center gap-2 text-teal-700">
                <CalendarIcon className="w-4 h-4" />
                Planificare Proiect
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Data Început</Label>
                  <Input
                    type="date"
                    value={siteSettings.start_date}
                    onChange={(e) => setSiteSettings({...siteSettings, start_date: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Data Sfârșit</Label>
                  <Input
                    type="date"
                    value={siteSettings.end_date}
                    onChange={(e) => setSiteSettings({...siteSettings, end_date: e.target.value})}
                  />
                </div>
              </div>
              <div className="mt-4">
                <Label>Stadiu Fizic (%)</Label>
                <div className="flex items-center gap-4">
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={siteSettings.progress_percent}
                    onChange={(e) => setSiteSettings({...siteSettings, progress_percent: parseFloat(e.target.value) || 0})}
                    className="w-24"
                  />
                  <div className="flex-1 h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-teal-500 to-green-500 transition-all"
                      style={{ width: `${siteSettings.progress_percent || 0}%` }}
                    />
                  </div>
                  <span className="text-sm font-semibold text-teal-700">{siteSettings.progress_percent || 0}%</span>
                </div>
              </div>
            </div>

            {/* Financial Section */}
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h4 className="font-semibold mb-3 flex items-center gap-2 text-green-700">
                <DollarSign className="w-4 h-4" />
                Aspect Financiar
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Valoare Totală Proiect (RON)</Label>
                  <Input
                    type="number"
                    value={siteSettings.total_value}
                    onChange={(e) => setSiteSettings({...siteSettings, total_value: parseFloat(e.target.value) || 0})}
                    placeholder="ex: 500000"
                  />
                </div>
                <div>
                  <Label>Cheltuieli Reale (RON)</Label>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      value={siteSettings.expenses}
                      readOnly
                      className="bg-slate-100"
                      placeholder="Calculat automat"
                    />
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={() => setShowExpensesBreakdown(true)}
                      className="whitespace-nowrap"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Adaugă
                    </Button>
                  </div>
                </div>
              </div>
              
              {/* Expenses Breakdown */}
              {expensesData && (
                <div className="mt-3 p-3 bg-white rounded-lg border space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-slate-600">Cheltuieli din Etape:</span>
                    <span className="text-sm font-semibold text-blue-600">
                      {new Intl.NumberFormat('ro-RO', { style: 'currency', currency: 'RON' }).format(expensesData.phases_expenses || 0)}
                    </span>
                  </div>
                  {expensesData.phases_breakdown?.filter(p => p.expenses > 0).map((p) => (
                    <div key={p.name} className="flex justify-between items-center pl-4 text-xs text-slate-500">
                      <span>• {p.name}</span>
                      <span>{new Intl.NumberFormat('ro-RO', { style: 'currency', currency: 'RON' }).format(p.expenses)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-slate-600">Cheltuieli Suplimentare:</span>
                    <span className="text-sm font-semibold text-orange-600">
                      {new Intl.NumberFormat('ro-RO', { style: 'currency', currency: 'RON' }).format(expensesData.extra_total || 0)}
                    </span>
                  </div>
                  <div className="border-t pt-2 flex justify-between items-center">
                    <span className="text-sm font-bold text-slate-700">TOTAL Cheltuieli:</span>
                    <span className={`text-sm font-bold ${expensesData.total_expenses > siteSettings.total_value * (siteSettings.progress_percent / 100) ? 'text-red-600' : 'text-green-600'}`}>
                      {new Intl.NumberFormat('ro-RO', { style: 'currency', currency: 'RON' }).format(expensesData.total_expenses || 0)}
                    </span>
                  </div>
                </div>
              )}
              
              {siteSettings.total_value > 0 && (
                <div className="mt-2 p-2 bg-white rounded border">
                  <p className="text-sm text-slate-600">
                    Buget proporțional ({siteSettings.progress_percent}%): {' '}
                    <span className="font-semibold">
                      {new Intl.NumberFormat('ro-RO', { style: 'currency', currency: 'RON' }).format(siteSettings.total_value * (siteSettings.progress_percent / 100))}
                    </span>
                  </p>
                </div>
              )}
            </div>

            {/* Work Schedule */}
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="font-semibold mb-3 flex items-center gap-2 text-blue-700">
                <Clock className="w-4 h-4" />
                Program de Lucru
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Ora Intrare</Label>
                  <Input
                    type="time"
                    value={siteSettings.work_start_time}
                    onChange={(e) => setSiteSettings({...siteSettings, work_start_time: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Ora Ieșire</Label>
                  <Input
                    type="time"
                    value={siteSettings.work_end_time}
                    onChange={(e) => setSiteSettings({...siteSettings, work_end_time: e.target.value})}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-3">
                <div>
                  <Label>Pauză Masă Început</Label>
                  <Input
                    type="time"
                    value={siteSettings.lunch_break_start}
                    onChange={(e) => setSiteSettings({...siteSettings, lunch_break_start: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Pauză Masă Sfârșit</Label>
                  <Input
                    type="time"
                    value={siteSettings.lunch_break_end}
                    onChange={(e) => setSiteSettings({...siteSettings, lunch_break_end: e.target.value})}
                  />
                </div>
              </div>
            </div>

            <div>
              <Label className="text-base font-semibold mb-3 block">Echipă Predefinită</Label>
              <ScrollArea className="h-48 border rounded-lg p-4">
                <div className="space-y-2">
                  {workers.map((worker) => (
                    <div key={worker.id} className="flex items-center space-x-3 p-2 hover:bg-slate-50 rounded">
                      <Checkbox
                        id={`team-${worker.id}`}
                        checked={siteSettings.assigned_team.includes(worker.id)}
                        onCheckedChange={() => handleTeamToggle(worker.id)}
                      />
                      <Label htmlFor={`team-${worker.id}`} className="flex-1 cursor-pointer">
                        <p className="font-medium">{worker.name}</p>
                        <p className="text-sm text-slate-600">{worker.email}</p>
                      </Label>
                    </div>
                  ))}
                </div>
              </ScrollArea>
              <p className="text-xs text-slate-600 mt-2">
                Angajații selectați vor primi pontaj automat dacă nu se înregistrează manual
              </p>
            </div>
            
            {/* Default Tools Section */}
            <div>
              <Label className="text-base font-semibold mb-3 block">Unelte Predefinite</Label>
              <ScrollArea className="h-48 border rounded-lg p-4">
                <div className="space-y-2">
                  {tools.map((tool) => (
                    <div key={tool.id} className="flex items-center space-x-3 p-2 hover:bg-slate-50 rounded">
                      <Checkbox
                        id={`tool-${tool.id}`}
                        checked={siteSettings.default_tools?.includes(tool.id) || false}
                        onCheckedChange={() => {
                          const currentTools = siteSettings.default_tools || [];
                          if (currentTools.includes(tool.id)) {
                            setSiteSettings({
                              ...siteSettings,
                              default_tools: currentTools.filter(id => id !== tool.id)
                            });
                          } else {
                            setSiteSettings({
                              ...siteSettings,
                              default_tools: [...currentTools, tool.id]
                            });
                          }
                        }}
                      />
                      <Label htmlFor={`tool-${tool.id}`} className="flex-1 cursor-pointer">
                        <p className="font-medium">{tool.name}</p>
                        {tool.description && <p className="text-sm text-slate-600">{tool.description}</p>}
                      </Label>
                    </div>
                  ))}
                </div>
              </ScrollArea>
              <p className="text-xs text-slate-600 mt-2">
                Uneltele selectate vor fi folosite automat dacă angajatul nu alege altele la pontare
              </p>
            </div>
            
            {/* Subcontractori Asignați */}
            {subcontractors.length > 0 && (
              <div>
                <Label className="text-base font-semibold mb-3 block">Subcontractori Asignați</Label>
                <ScrollArea className="h-48 border rounded-lg p-4">
                  <div className="space-y-2">
                    {subcontractors.map((sub) => (
                      <div key={sub.id} className="flex items-center space-x-3 p-2 hover:bg-orange-50 rounded border border-orange-100">
                        <Checkbox
                          id={`sub-${sub.id}`}
                          checked={siteSettings.assigned_subcontractors?.includes(sub.id) || false}
                          onCheckedChange={() => {
                            const currentSubs = siteSettings.assigned_subcontractors || [];
                            if (currentSubs.includes(sub.id)) {
                              setSiteSettings({
                                ...siteSettings,
                                assigned_subcontractors: currentSubs.filter(id => id !== sub.id)
                              });
                            } else {
                              setSiteSettings({
                                ...siteSettings,
                                assigned_subcontractors: [...currentSubs, sub.id]
                              });
                            }
                          }}
                        />
                        <Label htmlFor={`sub-${sub.id}`} className="flex-1 cursor-pointer">
                          <p className="font-medium">{sub.name}</p>
                          <p className="text-sm text-slate-600">{sub.employees?.length || 0} angajați</p>
                        </Label>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
                <p className="text-xs text-slate-600 mt-2">
                  Subcontractorii selectați vor fi asociați cu acest proiect
                </p>
              </div>
            )}
            
            {/* Project Phases Section */}
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <h4 className="font-semibold mb-3 flex items-center gap-2 text-purple-700">
                <Layers className="w-4 h-4" />
                Etape Proiect
              </h4>
              <p className="text-sm text-slate-600 mb-3">
                Împarte proiectul în etape/faze pentru o gestionare mai detaliată a progresului și costurilor.
              </p>
              <Button 
                type="button"
                onClick={() => {
                  setOpenSiteSettings(false);
                  setSelectedSiteForPhases(selectedSite);
                }}
                variant="outline" 
                className="w-full border-purple-300 text-purple-700 hover:bg-purple-100"
                data-testid="manage-phases-btn"
              >
                <Layers className="w-4 h-4 mr-2" />
                Gestionează Etapele ({selectedSite?.phases_count || 0} etape)
              </Button>
            </div>
            
            <Button onClick={handleSaveSiteSettings} className="w-full bg-teal-600 hover:bg-teal-700">
              Salvează Setări Proiect
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Adaugă Cheltuieli */}
      <Dialog open={showExpensesBreakdown} onOpenChange={setShowExpensesBreakdown}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-700">
              <Receipt className="w-5 h-5" />
              Adaugă Cheltuială Suplimentară
            </DialogTitle>
            <DialogDescription>
              Adaugă cheltuieli care nu sunt incluse în etapele de lucrări
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Formular adăugare */}
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200 space-y-3">
              <div>
                <Label>Descriere *</Label>
                <Input
                  value={newExpense.description}
                  onChange={(e) => setNewExpense({...newExpense, description: e.target.value})}
                  placeholder="Ex: Transport materiale, Închiriere utilaj..."
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Sumă (RON) *</Label>
                  <Input
                    type="number"
                    value={newExpense.amount}
                    onChange={(e) => setNewExpense({...newExpense, amount: parseFloat(e.target.value) || 0})}
                    placeholder="0"
                  />
                </div>
                <div>
                  <Label>Data</Label>
                  <Input
                    type="date"
                    value={newExpense.date}
                    onChange={(e) => setNewExpense({...newExpense, date: e.target.value})}
                  />
                </div>
              </div>
              <div>
                <Label>Categorie</Label>
                <Select value={newExpense.category} onValueChange={(v) => setNewExpense({...newExpense, category: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="transport">Transport</SelectItem>
                    <SelectItem value="materiale">Materiale</SelectItem>
                    <SelectItem value="utilaje">Utilaje</SelectItem>
                    <SelectItem value="manopera">Manoperă</SelectItem>
                    <SelectItem value="altele">Altele</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleAddExpense} className="w-full bg-orange-600 hover:bg-orange-700">
                <Plus className="w-4 h-4 mr-2" />
                Adaugă Cheltuială
              </Button>
            </div>
            
            {/* Lista cheltuieli existente */}
            {expensesData?.extra_expenses?.length > 0 && (
              <div className="border rounded-lg">
                <div className="p-3 bg-slate-50 border-b">
                  <h4 className="font-semibold text-sm text-slate-700">Cheltuieli Suplimentare Existente</h4>
                </div>
                <div className="max-h-48 overflow-y-auto">
                  {expensesData.extra_expenses.map((exp) => (
                    <div key={exp.id} className="flex items-center justify-between p-3 border-b last:border-b-0 hover:bg-slate-50">
                      <div>
                        <p className="font-medium text-sm">{exp.description}</p>
                        <p className="text-xs text-slate-500">{exp.date} • {exp.category}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-orange-600">
                          {new Intl.NumberFormat('ro-RO', { style: 'currency', currency: 'RON' }).format(exp.amount)}
                        </span>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleDeleteExpense(exp.id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Sumar */}
            <div className="bg-slate-100 p-3 rounded-lg">
              <div className="flex justify-between text-sm">
                <span>Total cheltuieli suplimentare:</span>
                <span className="font-bold text-orange-600">
                  {new Intl.NumberFormat('ro-RO', { style: 'currency', currency: 'RON' }).format(expensesData?.extra_total || 0)}
                </span>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Project Report Dialog */}
      <Dialog open={openProjectReport} onOpenChange={setOpenProjectReport}>
        <DialogContent className="max-w-5xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-blue-600" />
              Raport Proiect
            </DialogTitle>
          </DialogHeader>
          {selectedSiteForReport && (
            <ProjectReport 
              site={selectedSiteForReport} 
              onClose={() => setOpenProjectReport(false)} 
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Editare Prezență */}
      <Dialog open={!!editingAttendance} onOpenChange={(open) => !open && setEditingAttendance(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700">
              <Edit2 className="w-5 h-5" />
              Editează Prezența
            </DialogTitle>
            <DialogDescription>
              Modificați detaliile prezenței pentru {editingAttendance?.user_name}
            </DialogDescription>
          </DialogHeader>
          {editingAttendance && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-semibold">{editingAttendance.user_name}</p>
                <p className="text-sm text-slate-500">
                  {new Date(editingAttendance.check_in).toLocaleDateString('ro-RO', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                </p>
              </div>
              
              <div>
                <Label>Șantier</Label>
                <Select 
                  value={editAttendanceData.site_id} 
                  onValueChange={(v) => setEditAttendanceData({...editAttendanceData, site_id: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selectează șantierul" />
                  </SelectTrigger>
                  <SelectContent>
                    {sites.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Unelte utilizate</Label>
                <div className="grid grid-cols-1 gap-2 mt-2 max-h-48 overflow-y-auto">
                  {tools.map(tool => {
                    const availability = toolAvailability[tool.id];
                    const isUsedByOther = availability && !availability.is_available && 
                      availability.current_user !== editingAttendance?.user_name;
                    const isAlreadySelected = editAttendanceData.tool_ids.includes(tool.id);
                    
                    return (
                      <div key={tool.id} className={`p-2 rounded border ${isUsedByOther ? 'bg-red-50 border-red-200' : 'hover:bg-slate-50'}`}>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isAlreadySelected}
                            disabled={isUsedByOther && !isAlreadySelected}
                            onChange={(e) => {
                              const newTools = e.target.checked
                                ? [...editAttendanceData.tool_ids, tool.id]
                                : editAttendanceData.tool_ids.filter(id => id !== tool.id);
                              setEditAttendanceData({...editAttendanceData, tool_ids: newTools});
                            }}
                            className="rounded"
                          />
                          <div className="flex-1">
                            <span className={`text-sm font-medium ${isUsedByOther ? 'text-red-700' : ''}`}>
                              {tool.name}
                            </span>
                            <span className="text-xs text-slate-500 ml-2">({tool.total_hours?.toFixed(1) || 0}h)</span>
                          </div>
                        </label>
                        {isUsedByOther && (
                          <div className="mt-1 text-xs text-red-600 pl-6">
                            ⚠️ Ocupată de <strong>{availability.current_user}</strong> de la {availability.current_since}
                          </div>
                        )}
                        {availability?.usages_today?.length > 0 && !isUsedByOther && (
                          <div className="mt-1 text-xs text-slate-500 pl-6">
                            📋 Utilizări azi: {availability.usages_today.map(u => 
                              `${u.user_name} (${u.check_in}-${u.check_out})`
                            ).join(', ')}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editAttendanceData.uses_company_car}
                    onChange={(e) => setEditAttendanceData({...editAttendanceData, uses_company_car: e.target.checked})}
                    className="rounded"
                  />
                  <span className="text-sm">Mașină firmă</span>
                </label>
                {editAttendanceData.uses_company_car && (
                  <Select 
                    value={editAttendanceData.company_car_plate}
                    onValueChange={(v) => setEditAttendanceData({...editAttendanceData, company_car_plate: v})}
                  >
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Vehicul" />
                    </SelectTrigger>
                    <SelectContent>
                      {vehicles.map(v => (
                        <SelectItem key={v.id} value={v.plate}>{v.plate}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Ora Sosire</Label>
                  <Input
                    type="time"
                    value={editAttendanceData.check_in}
                    onChange={(e) => setEditAttendanceData({...editAttendanceData, check_in: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Ora Plecare</Label>
                  <Input
                    type="time"
                    value={editAttendanceData.check_out}
                    onChange={(e) => setEditAttendanceData({...editAttendanceData, check_out: e.target.value})}
                    placeholder="Lasă gol dacă nu a plecat"
                  />
                </div>
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button onClick={handleSaveAttendanceEdit} className="flex-1 bg-blue-600 hover:bg-blue-700">
                  Salvează Modificările
                </Button>
                <Button variant="outline" onClick={() => setEditingAttendance(null)} className="flex-1">
                  Anulează
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog pentru contractele șantierului */}
      <Dialog open={!!selectedSiteForContracts} onOpenChange={(open) => !open && setSelectedSiteForContracts(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-indigo-600" />
              Contracte - {selectedSiteForContracts?.name}
            </DialogTitle>
          </DialogHeader>
          
          <ScrollArea className="h-[400px] pr-4">
            {siteContracts.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">Nu există contracte pentru acest șantier</p>
                <p className="text-xs text-slate-400 mt-2">
                  Adăugați contracte din Documente → Contracte Prestări Servicii
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {siteContracts.map((doc) => (
                  <div 
                    key={doc.id}
                    className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className="bg-indigo-600 text-white">
                            #{doc.registration_number}
                          </Badge>
                          <Badge variant="outline" className="border-indigo-300 text-indigo-700">
                            {doc.document_type === 'contract_servicii' ? 'Contract Prestări' : 'Document'}
                          </Badge>
                        </div>
                        <h4 className="font-semibold">{doc.title}</h4>
                        {doc.description && (
                          <p className="text-sm text-slate-600 mt-1">{doc.description}</p>
                        )}
                        <p className="text-xs text-slate-500 mt-2">
                          Creat: {new Date(doc.created_at).toLocaleDateString('ro-RO', { 
                            day: 'numeric', 
                            month: 'long', 
                            year: 'numeric' 
                          })}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        {(doc.html_content || doc.file_url) && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                if (doc.html_content) {
                                  safeOpenHTMLWindow(doc.html_content);
                                } else if (doc.file_url) {
                                  window.open(doc.file_url, '_blank');
                                }
                              }}
                              className="text-blue-600"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                if (doc.file_url) window.open(doc.file_url, '_blank');
                              }}
                              className="text-indigo-600"
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </GlassLayout>
  );
}